import React, { useEffect, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'

import { SorterResult, TableRowSelection } from 'antd/lib/table/interface'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { IResearchFilter } from '../../model/types/ResearchFilter.interface'
import { selectResearchFilters } from '../../model/researchSlice'
import { useDispatch } from 'react-redux'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import {
  selectKeywordsFilter,
  selectKeywordsPagination,
  setKeywords,
  setKeywordsPagination,
} from '../../model/keywordsSlice'
import { IKeywordsFilter } from '../../model/types/KeywordsFilter.interface'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import { usePreviousValue } from '../../../../shared/lib/hooks/UsePreviousValue'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { useTranslation } from 'react-i18next'
import {
  GetResearchKeywordsApiArg,
  ResearchKeywordsItem,
  ResearchTableArg,
  useGetResearchKeywordsQuery,
  useExportKeywords,
} from '../../../../app/api'
import { AdvertisersCountCell } from '../../ui/cells/AdvertisersCountCell'
import { TopAdvertiserCell } from '../../ui/cells/TopAdvertiserCell'
import { AdsCountCell } from '../../ui/cells/AdsCountCell'
import clsx from 'clsx'

import styles from './KeywordsTable.module.scss'

export const KeywordsTable = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const researchFilters = useAppSelector<IResearchFilter>(selectResearchFilters)
  const keywordsFilter = useAppSelector<IKeywordsFilter>(selectKeywordsFilter)
  const pagination = useAppSelector<IPagination>(selectKeywordsPagination)
  const { current, pageSize, total } = pagination
  const [countRows, setCountRows] = useState<number>(0)
  const [queryParams, setQueryParams] = useState<
    Partial<ResearchTableArg<GetResearchKeywordsApiArg>>
  >({
    page: current,
    'per-page': pageSize,
  })
  const {
    keywords,
    dateRange: { date },
  } = keywordsFilter
  const { project, country, domain } = researchFilters
  const { data, isLoading, isFetching } = useGetResearchKeywordsQuery(
    queryParams as GetResearchKeywordsApiArg,
    {
      skip: !(queryParams?.projectIds as number[] | undefined)?.length,
    },
  )
  const previousProject = usePreviousValue(project)
  const [exportFile, { isFetching: exportKeywordsLoading }] = useExportKeywords()

  useEffect(() => {
    if (!data?.data) return
    setCountRows(data.data.total)

    if (!keywords) {
      dispatch(setKeywords((data.data.items ?? []).slice(0, 5).map((item) => item.keyword)))
    }
  }, [data])

  useEffect(() => {
    const project_ids = project ? [project] : []

    setQueryParams({
      ...queryParams,
      projectIds: project_ids as unknown as number[],
      'country_ids[]': country,
      domain,
      date,
    })
  }, [researchFilters, keywordsFilter])

  useEffect(() => {
    if (previousProject && project && previousProject !== project) {
      dispatch(setKeywords(undefined))
      dispatch(
        setKeywordsPagination({
          ...pagination,
          current: 1,
        }),
      )
    }
  }, [project])

  const columns: ColumnsType<ResearchKeywordsItem> = [
    {
      title: t('table.columns.keyword'),
      dataIndex: 'keyword',
      key: 'keyword',
      sorter: true,
    },
    {
      title: t('table.columns.advertisers'),
      dataIndex: 'sites_count',
      key: 'sites_count',
      render: (_: number, record) => <AdvertisersCountCell {...record} />,
      sorter: true,
    },
    {
      title: t('table.columns.topAdvertisers'),
      dataIndex: 'top_advertiser',
      key: 'top_advertiser',
      render: (_: string, record) => <TopAdvertiserCell {...record} />,
    },
    {
      title: () => (
        <span className={clsx(styles.titleWrap)}>
          {t('table.columns.ads')} <BpTooltip title={t('pages.advertisingResearch.adsTooltip')} />
        </span>
      ),
      dataIndex: 'ads_count',
      key: 'ads_count',
      render: (_: string, record) => <AdsCountCell {...record} />,
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('table.columns.firstFound'),
      key: 'first_link_at',
      dataIndex: 'first_link_at',
    },
    {
      title: t('table.columns.lastUpdate'),
      dataIndex: 'last_update',
      key: 'last_update',
      width: 150,
      sorter: true,
    },
  ]

  const rowSelection: TableRowSelection<ResearchKeywordsItem> = {
    selectedRowKeys: keywords,
    onChange: (selectedRowKeys) => {
      dispatch(setKeywords(selectedRowKeys as string[]))
    },
  }

  const handleTableChange = (
    { pageSize, current, total }: IBpPaginationProps,
    sorter?: SorterResult<ResearchKeywordsItem> | SorterResult<ResearchKeywordsItem>[],
  ) => {
    dispatch(
      setKeywordsPagination({
        pageSize: pageSize as number,
        current: current as number,
        total: total as number,
      }),
    )
    setQueryParams({
      ...queryParams,
      page: current,
      'per-page': pageSize,
      sort: useSorter(sorter),
    })
  }

  return (
    <section className={clsx(styles.wrapper)}>
      <div className={clsx(styles.header)}>
        <h3 className={clsx(styles.title)}>
          {t('pages.advertisingResearch.allKeywords')}: {countRows}
        </h3>
        <ExportDrop
          columns={[
            { label: t('table.columns.keyword'), keys: ['keyword'] },
            { label: t('table.columns.advertisers'), keys: ['sites_count'] },
            { label: t('table.columns.topAdvertisers'), keys: ['top_advertiser'] },
            { label: t('table.columns.ads'), keys: ['ads_count'] },
            { label: t('table.columns.firstFound'), keys: ['first_link_at'] },
            { label: t('table.columns.lastUpdate'), keys: ['last_update'] },
          ]}
          loading={exportKeywordsLoading}
          onExport={(exportParams) => exportFile({ ...queryParams, ...exportParams })}
        />
      </div>
      <BpTable
        pagination={{
          current,
          pageSize,
          total,
        }}
        columns={columns}
        data={data as TTableResponse<ResearchKeywordsItem>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={(record) => record.keyword}
        rowSelection={rowSelection}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      />
    </section>
  )
}
